import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { getTheme } from './theme';
import { apiFetch } from '../utils/api';

const ThemeContext = createContext(null);

const STORAGE_KEY = 'theme-mode';

/**
 * ThemeProvider - wraps the app and provides theme + toggle
 * Persists preference to localStorage and optionally to the database per user
 */
export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || 'dark';
    } catch {
      return 'dark';
    }
  });

  const theme = useMemo(() => getTheme(mode), [mode]);

  const toggleMode = useCallback(() => {
    setMode((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      // Only save to DB when user is logged in (avoids 401 on login page)
      if (localStorage.getItem('tms_token')) {
        apiFetch('/api/auth/theme', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ theme: next })
        }).catch(() => {});
      }
      return next;
    });
  }, []);

  // Apply user's saved preference (called after login)
  const applyUserTheme = useCallback((themePreference) => {
    if (themePreference && (themePreference === 'dark' || themePreference === 'light')) {
      setMode(themePreference);
    }
  }, []);

  // Persist to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, mode);
    } catch {
      // ignore
    }
  }, [mode]);

  // Update body background and CSS variables
  useEffect(() => {
    const c = theme.colors;
    document.body.style.background = c.bg;
    document.documentElement.style.setProperty('--bg', c.bg);
    document.documentElement.style.setProperty('--bg-card', c.bgCard);
    document.documentElement.style.setProperty('--bg-input', c.bgInput);
    document.documentElement.style.setProperty('--border', c.border);
    document.documentElement.style.setProperty('--text', c.text);
    document.documentElement.style.setProperty('--text-secondary', c.textSecondary);
  }, [theme]);

  const value = useMemo(() => ({ theme, mode, toggleMode, applyUserTheme }), [theme, mode, toggleMode, applyUserTheme]);

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * useTheme hook - access theme, mode, toggleMode, and applyUserTheme
 */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    return { theme: getTheme('dark'), mode: 'dark', toggleMode: () => {}, applyUserTheme: () => {} };
  }
  return ctx;
}
